// ============================================================================
// WHATSAPP DEL ESTUDIO — /whatsapp (12-oct-2026, portado de la agenda dental
// src/pages/WhatsAppStatusPage.tsx). Para que la dueña vincule el número del estudio.
//
// Flujo: tarjeta de estado → «Vincular WhatsApp» (pasa por el descargo si no está
// aceptado) → panel de QR con pasos, espera y expiración → conectado (Reiniciar /
// Desvincular). + Pausa de mensajes automáticos + Salud del número + guía anti-ban.
//
// Línea única (sin la doble línea MX/US de Denticate: Let's Move tiene un solo número).
// «¿Aprovisionado?» sale de GET /whatsapp-status → configured (ver services/whatsappApi).
// El QR se pide en polling a /whatsapp/qr SOLO en modo «vinculando»: en el backend ese GET
// se auto-recupera (crea/reinicia la sesión), así que jamás se llama al cargar la página.
// Solo dueña y gerente (role 'admin'); el menú ya lo oculta, y aquí se repite por si
// alguien entra por URL.
// ============================================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Smartphone, Wifi, WifiOff, RefreshCw, QrCode, CheckCircle2, AlertCircle, Loader2, Clock, LogOut, MessageCircle,
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/ConfirmDialog';
import WhatsAppHealthCard from './WhatsAppHealthCard';
import WhatsAppDisclaimerModal from './WhatsAppDisclaimerModal';
import WaPauseCard from './WaPauseCard';
import {
  checkSessionStatus, getQRCode, startSession, restartSession, logoutSession,
  acceptWhatsAppDisclaimer, requestWhatsAppActivation, getWhatsAppHealth,
  type WhatsAppSessionStatus, type WaHealth,
} from '../../services/whatsappApi';

const QR_POLL_INTERVAL = 4000;    // pide QR / detecta conexión al escanear
const QR_MAX_POLL_TIME = 180000;  // 3 min antes de pedir refresh manual

type Mode = 'loading' | 'connected' | 'idle' | 'linking' | 'error';

const cardCls = 'rounded-2xl border border-line bg-surface p-5 shadow-sm';
const btnVerde = 'flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60';
const btnSecundario = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-line bg-surface px-3.5 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2 disabled:opacity-50';
const btnPeligro = 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/5 px-3.5 py-2 text-sm font-medium text-red-600 transition hover:bg-red-500/10 disabled:opacity-50 dark:text-red-300';

function statusLabel(status: string | undefined, connected: boolean): string {
  if (connected) return 'WhatsApp conectado';
  const labels: Record<string, string> = {
    WORKING: 'WhatsApp conectado',
    SCAN_QR_CODE: 'Esperando escaneo de QR',
    STARTING: 'Iniciando…',
    STOPPED: 'WhatsApp no conectado',
    STOPPING: 'Deteniendo…',
    FAILED: 'WhatsApp no conectado',
    ERROR: 'Servicio no disponible',
  };
  return labels[status || ''] || 'WhatsApp no conectado';
}

export default function WhatsAppPage() {
  const { user } = useAuth();
  if (user && user.role !== 'admin') {
    return (
      <div className="w-full" data-testid="wa-sin-permiso">
        <h1 className="mb-3 font-display text-2xl font-bold text-content">WhatsApp del estudio</h1>
        <p className={`${cardCls} text-sm text-content-2`}>
          La conexión del WhatsApp del estudio la lleva la dueña o la gerente. Si algo no está saliendo, avísales a ellas.
        </p>
      </div>
    );
  }
  return <WhatsAppInner />;
}

function WhatsAppInner() {
  const toast = useToast();
  const [mode, setMode] = useState<Mode>('loading');
  const [st, setSt] = useState<WhatsAppSessionStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrExpired, setQrExpired] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [requested, setRequested] = useState(false);
  const [health, setHealth] = useState<WaHealth | null>(null);
  const [confirmLogout, setConfirmLogout] = useState(false);

  // Descargo (POR TENANT, compartido con agenda/manager)
  const [disclaimer, setDisclaimer] = useState<WhatsAppSessionStatus['disclaimer']>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [acceptingDisclaimer, setAcceptingDisclaimer] = useState(false);
  const disclaimerAccepted = !!disclaimer?.accepted;
  const disclaimerAcceptedAt = disclaimer?.acceptedAt ? new Date(disclaimer.acceptedAt) : null;

  const qrPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qrStartRef = useRef(0);
  const clearQrPolling = useCallback(() => {
    if (qrPollRef.current) { clearInterval(qrPollRef.current); qrPollRef.current = null; }
  }, []);
  useEffect(() => () => clearQrPolling(), [clearQrPolling]);

  // ── Estado ──
  const loadStatus = useCallback(async (showToast = false) => {
    setIsRefreshing(true);
    try {
      const s = await checkSessionStatus();
      setSt(s);
      setLoadError(null);
      setDisclaimer(s.disclaimer ?? null);
      setLastChecked(new Date());
      setNotConfigured(!s.configured);
      setRequested(s.requested === true);
      if (!s.configured) {
        clearQrPolling(); setQrCode(null); setMode('idle');
      } else if (s.connected) {
        clearQrPolling(); setQrCode(null); setMode('connected');
        if (showToast) toast.success('WhatsApp conectado');
      } else {
        setMode((m) => (m === 'linking' ? 'linking' : 'idle'));
        if (showToast) toast.info('WhatsApp no está conectado');
      }
      return s;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'No se pudo verificar el estado';
      setLoadError(msg);
      setMode((m) => (m === 'linking' ? 'linking' : 'error'));
      if (showToast) toast.error('No se pudo verificar el estado');
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, [clearQrPolling, toast]);

  // `toast` cambia de identidad en cada render del provider: se carga UNA vez al montar.
  const loadRef = useRef(loadStatus);
  loadRef.current = loadStatus;
  useEffect(() => {
    loadRef.current();
    getWhatsAppHealth().then(setHealth).catch(() => {});
  }, []);

  // ── Polling del QR (solo en modo vinculando) ──
  const startQrPolling = useCallback(() => {
    clearQrPolling();
    qrStartRef.current = Date.now();
    setQrExpired(false);
    const tick = async () => {
      if (Date.now() - qrStartRef.current > QR_MAX_POLL_TIME) {
        clearQrPolling(); setQrExpired(true); setQrCode(null);
        return;
      }
      try {
        const r = await getQRCode();
        if (r.notConfigured) {
          clearQrPolling(); setQrCode(null); setNotConfigured(true); setMode('idle');
          return;
        }
        if (r.isConnected) {
          clearQrPolling(); setQrCode(null); setMode('connected');
          toast.success('¡WhatsApp conectado!');
          loadRef.current();
        } else if (r.qrCode) {
          setQrCode(r.qrCode);
        }
      } catch { /* reintenta en el siguiente tick */ }
    };
    tick();
    qrPollRef.current = setInterval(tick, QR_POLL_INTERVAL);
  }, [clearQrPolling, toast]);

  // ── Acciones ──
  const doConnect = async () => {
    if (actionLoading) return;
    setActionLoading('connect');
    setQrCode(null);
    setMode('linking');
    // Igual que la agenda: aunque /start falle, el polling de /qr se auto-recupera en el
    // backend (crea/reinicia la sesión), así que se sigue pidiendo el código.
    await startSession();
    setActionLoading(null);
    startQrPolling();
  };

  // Compuerta del descargo: sin aceptación registrada no hay QR.
  const handleConnect = () => {
    if (disclaimerAccepted) { doConnect(); return; }
    setShowDisclaimer(true);
  };

  const handleAcceptDisclaimer = async () => {
    setAcceptingDisclaimer(true);
    const r = await acceptWhatsAppDisclaimer();
    setAcceptingDisclaimer(false);
    if (!r.success) { toast.error(r.message || 'No se pudo registrar la aceptación. Intenta de nuevo.'); return; }
    setDisclaimer({ accepted: true, acceptedAt: new Date().toISOString() });
    setShowDisclaimer(false);
    doConnect();
  };

  const handleRequestActivation = async () => {
    if (actionLoading) return;
    setActionLoading('request');
    const r = await requestWhatsAppActivation();
    setActionLoading(null);
    if (r.success) { setRequested(true); toast.success('Solicitud enviada. Te avisamos cuando esté lista para escanear.'); }
    else toast.error(r.message || 'No se pudo enviar la solicitud');
  };

  const handleRefreshQr = async () => {
    setQrExpired(false);
    await startSession();   // el polling reintenta igual si falla
    startQrPolling();
  };

  const handleCancelLinking = () => {
    clearQrPolling(); setQrCode(null); setQrExpired(false); setMode('idle');
    loadStatus();
  };

  const handleRestart = async () => {
    if (actionLoading) return;
    setActionLoading('restart');
    const r = await restartSession();
    setActionLoading(null);
    if (r.success) { toast.success('Sesión reiniciada'); setTimeout(() => loadRef.current(), 2000); }
    else toast.error(r.message || 'No se pudo reiniciar la sesión');
  };

  const doLogout = async () => {
    if (actionLoading) return;
    setActionLoading('logout');
    const r = await logoutSession();
    setActionLoading(null);
    if (r.success) toast.success('WhatsApp desvinculado');
    else toast.error(r.message || 'No se pudo desvincular');
    loadStatus();
  };

  // ── Render ──
  const isConnected = mode === 'connected';
  const phone = st?.deviceInfo?.id ? String(st.deviceInfo.id).split('@')[0].split(':')[0] : null;

  return (
    // 12-oct-2026 (Carlos): a TODO el ancho como el resto del panel (ruta densa 1600 en AppLayout); nada de max-w propio.
    <div className="w-full pb-10" data-testid="wa-page">
      <div className="mb-5">
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
          <MessageCircle size={24} className="text-emerald-500" /> WhatsApp del estudio
        </h1>
        <p className="mt-1 text-sm text-content-3">Conecta el número del estudio para que los mensajes automáticos a los alumnos salgan desde él.</p>
      </div>

      <div>
      {/* Una sola columna a lo ancho, todo hacia abajo (Carlos 12-oct: nada de columnas) */}
      <div>
      {/* Tarjeta de estado */}
      <div className={cardCls} data-testid="wa-status">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {mode === 'loading' ? <Loader2 size={24} className="shrink-0 animate-spin text-content-3" />
              : isConnected ? <Wifi size={24} className="shrink-0 text-emerald-500" />
              : <WifiOff size={24} className="shrink-0 text-content-3" />}
            <div className="min-w-0">
              <p className="font-semibold text-content" data-testid="wa-status-label">
                {mode === 'loading' ? 'Verificando conexión…'
                  : mode === 'error' ? 'No se pudo consultar el estado'
                  : notConfigured ? 'WhatsApp sin activar'
                  : statusLabel(st?.status, isConnected)}
              </p>
              {phone
                ? <p className="flex items-center gap-1 text-sm text-content-3"><Smartphone size={14} /> +{phone}</p>
                : <p className="text-sm text-content-3">Sin vincular</p>}
            </div>
          </div>
          {mode !== 'loading' && (
            <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
              isConnected ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-surface-2 text-content-3'}`}>
              {isConnected ? 'Conectado' : 'Desconectado'}
            </span>
          )}
        </div>

        {mode === 'error' && (
          <div className="mt-4 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">
            {loadError}. <button onClick={() => loadStatus(true)} className="font-semibold underline">Reintentar</button>
          </div>
        )}

        {isConnected && st?.deviceInfo?.pushName && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 size={16} /> Conectado como: <b>{st.deviceInfo.pushName}</b>
          </div>
        )}

        {/* Sin aprovisionar: no hay QR que pedir; se solicita la activación a plataforma */}
        {mode === 'idle' && notConfigured && (
          <div className="mt-4 space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
              <AlertCircle size={16} /> Tu WhatsApp todavía no está activado
            </p>
            <p className="text-sm text-content-2">
              {requested
                ? 'Ya recibimos tu solicitud. Te avisamos en cuanto esté lista y aquí mismo te aparecerá el código para escanear.'
                : 'Antes de escanear el código tenemos que preparar la conexión de tu número. Solicítala y te avisamos cuando esté lista.'}
            </p>
            {!requested && (
              <button onClick={handleRequestActivation} disabled={actionLoading === 'request'} className={btnVerde}>
                {actionLoading === 'request' ? <><Loader2 size={20} className="animate-spin" /> Enviando…</> : 'Solicitar activación'}
              </button>
            )}
          </div>
        )}

        {/* Vincular (no conectado y sin QR a la vista) */}
        {mode === 'idle' && !notConfigured && (
          <button onClick={handleConnect} disabled={actionLoading === 'connect'} className={`mt-4 ${btnVerde}`} data-testid="wa-vincular">
            {actionLoading === 'connect'
              ? <><Loader2 size={20} className="animate-spin" /> Generando QR…</>
              : <><QrCode size={20} /> Vincular WhatsApp</>}
          </button>
        )}

        {isConnected && (
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={handleRestart} disabled={!!actionLoading} className={btnSecundario}>
              {actionLoading === 'restart' ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Reiniciar
            </button>
            <button onClick={() => setConfirmLogout(true)} disabled={!!actionLoading} className={btnPeligro}>
              {actionLoading === 'logout' ? <Loader2 size={16} className="animate-spin" /> : <LogOut size={16} />} Desvincular
            </button>
          </div>
        )}
      </div>

      {/* Panel de QR */}
      {mode === 'linking' && (
        <div className={`mt-5 space-y-4 text-center ${cardCls}`}>
          <h2 className="font-display text-base font-bold text-content">Escanea el código con WhatsApp</h2>
          <ol className="mx-auto max-w-xs list-inside list-decimal space-y-1 text-left text-sm text-content-2">
            <li>Abre WhatsApp en el teléfono del estudio</li>
            <li>Toca <b className="text-content">Ajustes → Dispositivos vinculados</b></li>
            <li>Toca <b className="text-content">Vincular un dispositivo</b> y escanea</li>
          </ol>
          <div className="flex justify-center">
            {qrCode ? (
              <img src={qrCode} alt="Código QR de WhatsApp" className="h-56 w-56 rounded-xl border border-line bg-white p-2" />
            ) : qrExpired ? (
              <div className="flex h-56 w-56 flex-col items-center justify-center gap-2 rounded-xl border border-line text-content-3">
                <Clock size={32} /><span className="text-sm">El QR expiró</span>
              </div>
            ) : (
              <div className="flex h-56 w-56 items-center justify-center rounded-xl border border-line">
                <Loader2 size={32} className="animate-spin text-emerald-500" />
              </div>
            )}
          </div>
          {qrExpired ? (
            <button onClick={handleRefreshQr} className="inline-flex items-center gap-2 font-medium text-emerald-600 dark:text-emerald-400">
              <RefreshCw size={16} /> Generar un nuevo QR
            </button>
          ) : (
            <p className="flex items-center justify-center gap-1 text-xs text-content-3">
              <Loader2 size={12} className="animate-spin" /> Esperando a que escanees… el código se renueva solo
            </p>
          )}
          <div>
            <button onClick={handleCancelLinking} className="text-sm text-content-3 hover:text-content">Cancelar</button>
          </div>
        </div>
      )}

      {/* Última verificación */}
      {lastChecked && (
        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-content-3">
          <button onClick={() => loadStatus(true)} disabled={isRefreshing} className="inline-flex items-center gap-1 hover:text-content disabled:opacity-50">
            <RefreshCw size={12} className={isRefreshing ? 'animate-spin' : ''} /> Actualizar
          </button>
          <span className="flex items-center gap-1"><Clock size={12} /> Última verificación: {lastChecked.toLocaleTimeString('es-MX')}</span>
        </div>
      )}

      </div>

      <div>
      {/* Pausa de mensajes automáticos — la alternativa a desvincular */}
      {!notConfigured && mode !== 'loading' && <WaPauseCard />}

      {/* Salud del número (foto diaria) */}
      {health && !notConfigured && <WhatsAppHealthCard health={health} />}

      </div>

      <div>
      {/* Guía anti-ban */}
      <div className="mt-5 space-y-2 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
        <h4 className="flex items-center gap-2 font-semibold text-amber-700 dark:text-amber-300">
          <AlertCircle size={18} /> Importante: evita el bloqueo de tu número
        </h4>
        <p className="text-sm text-content-2">
          WhatsApp puede suspender temporal o permanentemente los números que detecte enviando mensajes automatizados de
          forma abusiva. Para proteger tu cuenta:
        </p>
        <ul className="space-y-1.5 text-sm text-content-2">
          <li className="flex gap-2"><span className="text-amber-500">•</span><span><b className="text-content">Usa un número dedicado al estudio,</b> no tu número personal. Si WhatsApp lo suspende, no pierdes tu cuenta personal.</span></li>
          <li className="flex gap-2"><span className="text-amber-500">•</span><span><b className="text-content">No envíes spam.</b> Escribe solo a alumnos y familias que conocen el estudio y esperan tus mensajes.</span></li>
          <li className="flex gap-2"><span className="text-amber-500">•</span><span><b className="text-content">No envíes mensajes idénticos</b> a muchas personas seguidas.</span></li>
          <li className="flex gap-2"><span className="text-amber-500">•</span><span><b className="text-content">Si alguien te bloquea, no insistas.</b> Los bloqueos frecuentes son una señal de alerta para WhatsApp.</span></li>
        </ul>
        <p className="pt-1 text-xs text-content-3">
          La plataforma incluye protecciones automáticas (conexión dedicada y pausas entre mensajes), pero el uso
          responsable es tu mejor protección.
        </p>
      </div>

      {/* ¿Cómo funciona? */}
      <div className="mt-5 rounded-xl border border-brand-blue/30 bg-brand-blue/5 p-4">
        <h4 className="mb-2 flex items-center gap-2 font-semibold text-content"><AlertCircle size={16} className="text-brand-blue" /> ¿Cómo funciona?</h4>
        <ul className="space-y-1 text-sm text-content-2">
          <li>• Al vincular, los mensajes automáticos del estudio salen desde este número</li>
          <li>• El código QR se renueva automáticamente mientras esperas</li>
          <li>• Si el problema persiste, escríbenos desde «Mis peticiones»</li>
        </ul>
      </div>
      </div>
      </div>

      {disclaimerAcceptedAt && (
        <p className="mt-4 text-center text-xs text-content-3">
          Aceptaste el aviso de uso responsable el {disclaimerAcceptedAt.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </p>
      )}

      {showDisclaimer && (
        <WhatsAppDisclaimerModal onAccept={handleAcceptDisclaimer} onCancel={() => setShowDisclaimer(false)} accepting={acceptingDisclaimer} />
      )}
      {confirmLogout && (
        <ConfirmDialog
          title="¿Desvincular WhatsApp?"
          lines={['Los mensajes automáticos del estudio dejarán de salir hasta volver a escanear el QR.']}
          warning="Si solo quieres que no salgan mensajes un rato, usa «Pausar mensajes automáticos»: no hace falta desvincular."
          confirmLabel="Desvincular"
          onConfirm={doLogout}
          onClose={() => setConfirmLogout(false)}
        />
      )}
    </div>
  );
}
